import { useQuery } from '@tanstack/react-query';
import { getNotificationSummary } from '../api/notifications';
import { useAuth } from '../context/authContext';
import { pollInterval } from '../utils/rateLimit';

/** How often the app checks for notifications (2026-11-03: was three
 * separate polls every 8s / 15s / 30s in every tab). */
export const NOTIFICATION_POLL_MS = 60_000;

/** Role-based notification/pending-action indicator system — one shared
 * query backing every sidebar badge, the bell's unread count and (through
 * `latestUnreadId`) the toast watcher, instead of a separate query per
 * icon (§12).
 *
 * Notification 429 investigation (2026-11-03): this is the app's only
 * notification poll. Exactly one component passes `poll` (AppLayout), so a
 * tab makes one request a minute however many places read the summary —
 * every other caller just reads the shared cache. React Query pauses the
 * poll while the tab is hidden, never overlaps it with a request still in
 * flight, and checks once on return to the tab if the figures are over 30s
 * old. Disabled while logged out, so no request fires before there's a
 * token to send; a 429 waits out the server's Retry-After. */
export const useNotificationSummary = ({ poll = false }: { poll?: boolean } = {}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['notification-summary'],
    queryFn: getNotificationSummary,
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: poll
      ? (query) => pollInterval(NOTIFICATION_POLL_MS, query.state.error)
      : false,
    refetchOnWindowFocus: poll,
  });
};
